import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import BrandName from '../components/BrandName';
import MainNavigation from '../components/MainNavigation';
import Footer from '../components/Footer';
import ThreeDPreview from '../components/ThreeDPreview';
import LoginModal from '../components/LoginModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Upload, Package, Shield, Settings, Check, ChevronRight, ChevronLeft, Printer, DollarSign, Boxes, ShoppingCart, Eye, User, Edit, BarChart3, Users, Mail, Phone, MapPin, Clock, Star, Heart, X, CheckCircle, AlertCircle, Info, Truck, CreditCard, LogIn, Menu, Search, Filter, Download, Home, BookOpen, HelpCircle, FileQuestion, Palette, ArrowLeft, ArrowRight, DraftingCompass, Layers, FileText, Combine, Rotate3d, Lock, Flame, Droplets, Zap, Wind, Sparkles } from 'lucide-react';
import { trackQuoteSubmission, trackCheckout, trackPageView } from '../utils/analytics';
import { BACKEND_URL } from '../config';

const API = `${BACKEND_URL}/api`;

// Material Swatch Selector Component - Stylized Expandable List
const MaterialSwatchSelector = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  
  const materials = [
    { 
      id: 'PLA', 
      name: 'PLA', 
      subtitle: 'Standard',
      color: '#10b981',
      gradient: 'from-emerald-400 to-emerald-500',
      bestFor: 'Display models',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/j5yye52r_PLA.jpg'
    },
    { 
      id: 'PLA+', 
      name: 'PLA+', 
      subtitle: 'Reinforced',
      color: '#059669',
      gradient: 'from-emerald-500 to-emerald-600',
      bestFor: 'Light functional parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/j5yye52r_PLA.jpg'
    },
    { 
      id: 'Silk PLA', 
      name: 'Silk PLA', 
      subtitle: 'Glossy finish',
      color: '#ec4899',
      gradient: 'from-pink-400 via-purple-400 to-cyan-400',
      bestFor: 'Visual pieces',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/4vfqnk5j_Silk%20PLA.jpg'
    },
    { 
      id: 'High Temp PLA', 
      name: 'High Temp PLA', 
      subtitle: 'Heat resistant',
      color: '#dc2626',
      gradient: 'from-red-500 to-red-600',
      bestFor: 'Heat-exposed PLA parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/6c7xebfo_High%20temp%20PLA.jpg'
    },
    { 
      id: 'PLA-CF', 
      name: 'PLA-CF', 
      subtitle: 'Carbon Fiber',
      color: '#1e293b',
      gradient: 'from-slate-700 to-slate-900',
      bestFor: 'Structural parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/cjf1rm33_CF%20PLA.jpg'
    },
    { 
      id: 'Metal-filled PLA', 
      name: 'Metal-filled PLA', 
      subtitle: 'Metal finish',
      color: '#71717a',
      gradient: 'from-zinc-400 to-zinc-600',
      bestFor: 'Decorative pieces',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/nx3yf7li_Metallic%20PLA.jpg'
    },
    { 
      id: 'Wood-filled PLA', 
      name: 'Wood-filled PLA', 
      subtitle: 'Wood texture',
      color: '#a16207',
      gradient: 'from-amber-600 to-amber-800',
      bestFor: 'Decorative wood pieces',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/u0syufl0_Wood%20PLA.jpg'
    },
    { 
      id: 'PETG', 
      name: 'PETG', 
      subtitle: 'Durable',
      color: '#0ea5e9',
      gradient: 'from-sky-400 to-sky-500',
      bestFor: 'Functional parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/o7syjr57_PETG.jpg'
    },
    { 
      id: 'PETG-CF', 
      name: 'PETG-CF', 
      subtitle: 'Carbon Fiber',
      color: '#334155',
      gradient: 'from-slate-600 to-slate-800',
      bestFor: 'Functional Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/r6pvkspz_PETG%20CF.jpg'
    },
    { 
      id: 'ABS', 
      name: 'ABS', 
      subtitle: 'Standard',
      color: '#64748b',
      gradient: 'from-slate-400 to-slate-500',
      bestFor: 'General Mechanical Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/3qn26xtd_ABS.jpg'
    },
    { 
      id: 'ABS+', 
      name: 'ABS+', 
      subtitle: 'Reinforced',
      color: '#475569',
      gradient: 'from-slate-500 to-slate-600',
      bestFor: 'General Mechanical Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/3qn26xtd_ABS.jpg'
    },
    { 
      id: 'ASA', 
      name: 'ASA', 
      subtitle: 'UV Resistant',
      color: '#f59e0b',
      gradient: 'from-amber-400 to-amber-500',
      bestFor: 'Mechanical and Functional Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/cslexh2j_ASA.jpg'
    },
    { 
      id: 'TPU 95A', 
      name: 'TPU 95A', 
      subtitle: 'Standard Flexible',
      color: '#f97316',
      gradient: 'from-orange-400 to-orange-500',
      bestFor: 'Bushings, joints',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/jlt30i3g_TPU.jpg'
    },
    { 
      id: 'TPU 64D', 
      name: 'TPU 64D', 
      subtitle: 'Semi-rigid',
      color: '#ea580c',
      gradient: 'from-orange-500 to-orange-600',
      bestFor: 'Gaskets, grips',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/rmp5di0p_TPU%2064D.jpg'
    },
    { 
      id: 'TPU 85A', 
      name: 'TPU 85A', 
      subtitle: 'Soft Flexible',
      color: '#c2410c',
      gradient: 'from-orange-600 to-orange-700',
      bestFor: 'Soft grips, seals',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/fa8oj5bd_TPU%2085A.jpg'
    },
  ];
  
  const selectedMaterial = materials.find(m => m.id === value);
  
  return (
    <div className="relative mt-2">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-white border-2 rounded-xl transition-all duration-300 text-left group ${
          isOpen ? 'border-[#0007b5] shadow-lg shadow-blue-500/10' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        {selectedMaterial ? (
          <div className="flex items-center gap-3">
            {selectedMaterial.thumbnail ? (
              <img 
                src={selectedMaterial.thumbnail} 
                alt={selectedMaterial.name}
                className="w-10 h-10 rounded-xl object-cover shadow-lg transition-transform duration-300 group-hover:scale-110"
              />
            ) : (
              <div 
                className={`w-10 h-10 rounded-xl bg-gradient-to-br ${selectedMaterial.gradient} shadow-lg transition-transform duration-300 group-hover:scale-110`}
                style={{ boxShadow: `0 4px 14px ${selectedMaterial.color}40` }}
              ></div>
            )}
            <div>
              <span className="font-semibold text-slate-900">{selectedMaterial.name}</span>
              <span className="text-slate-400 ml-2 text-sm">{selectedMaterial.subtitle}</span>
            </div>
          </div>
        ) : (
          <span className="text-slate-400">Choose material</span>
        )}
        <div className={`w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#0007b5] rotate-180' : 'group-hover:bg-slate-200'}`}>
          <ChevronRight className={`w-4 h-4 transition-all duration-300 ${isOpen ? 'text-white rotate-90' : 'text-slate-400'}`} />
        </div>
      </button>
      
      {/* Expandable Panel with Fluid Animation */}
      <div 
        className={`overflow-hidden transition-all duration-500 ease-out ${
          isOpen ? 'max-h-[700px] opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'
        }`}
      >
        <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-xl overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold text-slate-900">Select Material</h4>
                <p className="text-xs text-slate-500 mt-0.5">Choose the best material for your project</p>
              </div>
              <a href="/material-guide" className="text-xs text-[#0007b5] hover:underline flex items-center gap-1 font-medium">
                <BookOpen className="w-3 h-3" />
                Full Guide
              </a>
            </div>
          </div>
          
          {/* Material List - Stylized */}
          <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
            {materials.map((material, index) => {
              const isSelected = value === material.id;
              const isHovered = hoveredId === material.id;
              return (
                <button
                  key={material.id}
                  type="button"
                  onClick={() => {
                    onChange(material.id);
                    setIsOpen(false);
                  }}
                  onMouseEnter={() => setHoveredId(material.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all duration-300 text-left group relative ${
                    isSelected 
                      ? 'bg-[#0007b5] shadow-lg' 
                      : isHovered
                        ? 'bg-slate-50'
                        : 'bg-white hover:bg-slate-50'
                  }`}
                  style={{
                    transitionDelay: `${index * 30}ms`,
                    transform: isOpen ? 'translateX(0)' : 'translateX(-20px)',
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  {/* Color Swatch or Thumbnail with Glow Effect */}
                  <div className="relative flex-shrink-0">
                    {material.thumbnail ? (
                      <img 
                        src={material.thumbnail} 
                        alt={material.name}
                        className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-xl object-contain bg-slate-50 shadow-lg transition-all duration-300 ${isHovered || isSelected ? 'scale-105' : ''}`}
                        style={{ 
                          boxShadow: isHovered || isSelected ? `0 8px 24px ${material.color}50` : `0 4px 12px ${material.color}30`
                        }}
                      />
                    ) : (
                      <div 
                        className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-xl bg-gradient-to-br ${material.gradient} shadow-lg transition-all duration-300 ${isHovered || isSelected ? 'scale-105' : ''}`}
                        style={{ 
                          boxShadow: isHovered || isSelected ? `0 8px 24px ${material.color}50` : `0 4px 12px ${material.color}30`
                        }}
                      ></div>
                    )}
                    {isSelected && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center shadow-md">
                        <Check className="w-3 h-3 sm:w-4 sm:h-4 text-[#0007b5]" />
                      </div>
                    )}
                  </div>
                  
                  {/* Material Info - Stacked layout on mobile, side-by-side on desktop */}
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    {/* Mobile: Fully stacked layout */}
                    <div className="md:hidden">
                      <div className={`text-sm font-semibold transition-colors duration-300 ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                        {material.name}
                      </div>
                      <div className={`text-xs transition-colors duration-300 ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>
                        {material.subtitle}
                      </div>
                      <div className={`mt-1 transition-all duration-300 ${isHovered || isSelected ? 'opacity-100' : 'opacity-70'}`}>
                        <span className={`text-[9px] uppercase tracking-wide ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>Common Use</span>
                        <div className={`text-[11px] font-medium ${isSelected ? 'text-white' : 'text-slate-600'}`}>{material.bestFor}</div>
                      </div>
                    </div>
                    
                    {/* Desktop: Title and subtitle stacked */}
                    <div className="hidden md:block">
                      <div className={`font-semibold transition-colors duration-300 ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                        {material.name}
                      </div>
                      <div className={`text-sm transition-colors duration-300 ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>
                        {material.subtitle}
                      </div>
                    </div>
                  </div>
                  
                  {/* Common Use Badge - Right Side (desktop only) */}
                  <div className={`hidden md:block text-right flex-shrink-0 transition-all duration-300 ${isHovered || isSelected ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'}`}>
                    <span className={`text-[10px] uppercase tracking-wide ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>Common Use</span>
                    <p className={`text-xs font-medium ${isSelected ? 'text-white' : 'text-slate-600'}`}>{material.bestFor}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Generic Styled Selector Component - Expandable List Style
const StyledSelector = ({ value, onChange, options, placeholder = "Select option" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  
  const selectedOption = options.find(o => o.id === value);
  
  return (
    <div className="relative mt-2">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-white border-2 rounded-xl transition-all duration-300 text-left group ${
          isOpen ? 'border-[#0007b5] shadow-lg shadow-blue-500/10' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        {selectedOption ? (
          <div className="flex items-center gap-3">
            {selectedOption.thumbnail && (
              <img 
                src={selectedOption.thumbnail} 
                alt={selectedOption.name}
                className="w-8 h-8 rounded-lg object-cover shadow-sm"
              />
            )}
            <span className="font-medium text-slate-900">{selectedOption.name}</span>
            {selectedOption.subtitle && (
              <span className="text-slate-400 text-sm">{selectedOption.subtitle}</span>
            )}
          </div>
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
        <div className={`w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#0007b5] rotate-180' : 'group-hover:bg-slate-200'}`}>
          <ChevronRight className={`w-4 h-4 transition-all duration-300 ${isOpen ? 'text-white rotate-90' : 'text-slate-400'}`} />
        </div>
      </button>
      
      {/* Expandable Panel */}
      <div 
        className={`overflow-hidden transition-all duration-500 ease-out ${
          isOpen ? 'max-h-[400px] opacity-100 mt-2' : 'max-h-0 opacity-0 mt-0'
        }`}
      >
        <div className="bg-white rounded-xl border-2 border-slate-100 shadow-xl overflow-hidden">
          <div className="p-2 space-y-1 max-h-[300px] overflow-y-auto">
            {options.map((option, index) => {
              const isSelected = value === option.id;
              const isHovered = hoveredId === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    onChange(option.id);
                    setIsOpen(false);
                  }}
                  onMouseEnter={() => setHoveredId(option.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 text-left ${
                    isSelected 
                      ? 'bg-[#0007b5]' 
                      : isHovered
                        ? 'bg-slate-50'
                        : 'bg-white hover:bg-slate-50'
                  }`}
                  style={{
                    transitionDelay: `${index * 20}ms`,
                    transform: isOpen ? 'translateX(0)' : 'translateX(-10px)',
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  {/* Thumbnail or Checkmark */}
                  {option.thumbnail ? (
                    <div className="relative">
                      <img 
                        src={option.thumbnail} 
                        alt={option.name}
                        className={`w-12 h-12 rounded-lg object-cover shadow-md transition-all duration-300 ${isHovered || isSelected ? 'scale-110' : ''}`}
                      />
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md">
                          <Check className="w-3 h-3 text-[#0007b5]" />
                        </div>
                      )}
                    </div>
                  ) : isSelected ? (
                    <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-[#0007b5]" />
                    </div>
                  ) : null}
                  
                  {/* Option Info */}
                  <div className="flex-1 min-w-0">
                    <span className={`font-medium transition-colors duration-300 ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                      {option.name}
                    </span>
                    {option.subtitle && (
                      <span className={`ml-2 text-sm transition-colors duration-300 ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>
                        {option.subtitle}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};


const ComingSoonPage = ({ onAuthenticate }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Get password from environment or use default
    const correctPassword = process.env.REACT_APP_SITE_PASSWORD || 'launch2025';
    
    if (password === correctPassword) {
      sessionStorage.setItem('siteAuthenticated', 'true');
      onAuthenticate();
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4 py-12">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-white mb-4">
            <BrandName size="3xl" className="uppercase" />
          </h1>
          <p className="text-xl text-blue-200 mb-2">Coming Soon</p>
          <p className="text-slate-300">
            We&apos;re putting the final touches on something amazing. Enter the password to preview.
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter password"
                required
              />
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-[#0007b5] hover:bg-[#000690] text-white font-semibold py-3 rounded-lg transition-colors"
            >
              Enter Site
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/20 text-center">
            <p className="text-sm text-slate-300">
              Don&apos;t have the password? <a href="mailto:hello@create3dworks.com" className="text-blue-400 hover:text-blue-300">Contact us</a>
            </p>
          </div>
        </div>

        {/* Professional Designer Invitation Card */}
        <div className="mt-8 bg-gradient-to-br from-blue-600/20 to-purple-600/20 backdrop-blur-md rounded-2xl p-6 border border-blue-400/30">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Palette className="w-6 h-6 text-blue-300" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">
                Professional 3D Designers Welcome
              </h3>
              <p className="text-sm text-slate-300 mb-4">
                Join our marketplace and showcase your 3D designs to customers worldwide. Earn revenue from every sale.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-200 border border-green-400/30">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Passive Income
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-200 border border-green-400/30">
                  <BarChart3 className="w-3 h-3 mr-1" />
                  Greater Reach
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-200 border border-green-400/30">
                  <Shield className="w-3 h-3 mr-1" />
                  IP Protected
                </span>
              </div>
              <a
                href="mailto:designers@create3dworks.com?subject=Designer Application Inquiry"
                className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Apply as Designer
                <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-slate-400 text-sm">
          <p>© 2026 <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span>. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};


const AppContent = () => {
  const navigate = useNavigate();
  const { cart, addToCart, clearCart } = useCart();
  const { user, isAuthenticated, loginWithGoogle, logout, loading, getIdToken } = useAuth();
  const { formatPrice } = useCurrency();
  
  const [activeTab, setActiveTab] = useState("upload");
  const [quoteData, setQuoteData] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [leadTimeAdjustment, setLeadTimeAdjustment] = useState({ value: 0, unit: 'days' });
  const [discountSettings, setDiscountSettings] = useState({ hidden_discount: 0, visible_discount: 0, visible_discount_name: 'Promotional Pricing' });
  const [detectedUnits, setDetectedUnits] = useState({ units: null, message: null });
  
  // Instant Quote Wizard State
  const [quoteStep, setQuoteStep] = useState(0);
  const [quoteStepDirection, setQuoteStepDirection] = useState(1);

  // Scroll-triggered animation for "Flexible Solutions" cards (mirrors About
  // page "What drives us" behaviour): cards stay hidden until the grid
  // enters the viewport, then slide in alternately with staggered delays.
  const flexibleSolutionsRef = useRef(null);
  const [flexibleVisible, setFlexibleVisible] = useState(false);
  useEffect(() => {
    const node = flexibleSolutionsRef.current;
    if (!node || flexibleVisible) return;
    if (typeof IntersectionObserver === 'undefined') {
      setFlexibleVisible(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setFlexibleVisible(true);
          io.disconnect();
        }
      });
    }, { threshold: 0.15 });
    io.observe(node);
    return () => io.disconnect();
  }, [flexibleVisible]);
  
  // Define steps for Instant Quote wizard
  const quoteSteps = [
    { title: "Material & Quality", icon: Settings, fields: ["material", "quality"] },
    { title: "Options", icon: Layers, fields: ["strength", "smoothing"] },
    { title: "Color & Quantity", icon: Palette, fields: ["color", "quantity"] },
    { title: "Contact Info", icon: User, fields: ["contact_name", "contact_email"] },
    { title: "Shipping", icon: MapPin, fields: ["country_code", "postal_code"] },
    { title: "Review & Submit", icon: FileText, fields: ["notes"] }
  ];
  
  const totalQuoteSteps = quoteSteps.length;
  const quoteProgress = ((quoteStep + 1) / totalQuoteSteps) * 100;
  
  const goToQuoteStep = (step) => {
    setQuoteStepDirection(step > quoteStep ? 1 : -1);
    setQuoteStep(step);
  };
  
  const nextQuoteStep = () => {
    if (quoteStep < totalQuoteSteps - 1) {
      setQuoteStepDirection(1);
      setQuoteStep(prev => prev + 1);
    }
  };
  
  const prevQuoteStep = () => {
    if (quoteStep > 0) {
      setQuoteStepDirection(-1);
      setQuoteStep(prev => prev - 1);
    }
  };
  
  const [formData, setFormData] = useState({
    file: null,
    material: "",
    color: "Any",
    customColor: "",
    quality: "",
    strength: "",
    smoothing: "",
    contact_name: "",  // Contact name
    contact_email: "",
    notes: "",
    units: "mm",  // Default to millimeters
    country_code: "CA",  // Default shipping to Canada (domestic)
    postal_code: "",  // Optional postal/zip code
    existing_quote_id: null,  // For preserving file when editing quote
    preserved_filename: null,  // Filename of preserved file when editing
    quantity: 1  // Quantity for bulk orders with discount
  });

  // Check for email verification redirect
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('verified') === 'true') {
      setEmailVerified(true);
      setShowLoginModal(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Fetch lead time adjustment on mount
  React.useEffect(() => {
    const fetchLeadTimeAdjustment = async () => {
      try {
        const response = await fetch(`${API}/lead-time-adjustment`);
        if (response.ok) {
          const data = await response.json();
          setLeadTimeAdjustment(data);
        }
      } catch (error) {
        console.error('Error fetching lead time adjustment:', error);
      }
    };
    fetchLeadTimeAdjustment();
  }, []);

  // Fetch discount settings on mount
  React.useEffect(() => {
    const fetchDiscountSettings = async () => {
      try {
        const response = await fetch(`${API}/discount-settings`);
        if (response.ok) {
          const data = await response.json();
          setDiscountSettings(data);
        }
      } catch (error) {
        console.error('Error fetching discount settings:', error);
      }
    };
    fetchDiscountSettings();
  }, []);

  // Handle hash-based navigation on page load
  React.useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 100;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  // Check for payment status on page load
  React.useEffect(() => {
    // Wait for Firebase auth to finish loading
    if (loading) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentParam = urlParams.get('payment');

    if (sessionId && paymentParam === 'success') {
      // User returned from successful Stripe checkout
      if (isAuthenticated) {
        setPaymentStatus('checking');
        pollPaymentStatus(sessionId);
      } else {
        // User is not authenticated, might have been logged out during payment
        setPaymentError('Please log in to check your payment status.');
      }
    } else if (paymentParam === 'cancelled') {
      setPaymentError('Payment was cancelled. You can try again anytime.');
    }
  }, [loading, isAuthenticated]); // Add dependencies to wait for auth state

  // Function to poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 3000; // 3 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setPaymentError('Payment verification timed out. Please check your email or profile for order status.');
      return;
    }

    try {
      // Get Firebase ID token for authenticated request
      const idToken = await getIdToken();
      if (!idToken) {
        // If we can't get the token, wait a bit and retry (in case auth is still loading)
        if (attempts < 3) {
          console.log(`Waiting for authentication... attempt ${attempts + 1}`);
          setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
          return;
        } else {
          setPaymentStatus('error');
          setPaymentError('Unable to verify authentication. Please log in and check your profile for order status.');
          return;
        }
      }

      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          setPaymentError(null);
          
          // Track order completion in Google Analytics
          if (data.order_id && data.total_amount) {
            trackOrderCreation({
              order_id: data.order_id,
              total_price: data.total_amount,
              quote_id: data.quote_id,
              item_name: data.item_name || 'Print Order'
            });
          }
          
          // Clear the cart after successful payment
          clearCart();
          console.log('Payment successful - cart cleared');
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        } else if (data.status === 'expired') {
          setPaymentStatus('failed');
          setPaymentError('Payment session expired. Please try again.');
          return;
        }

        // If payment is still pending, continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        throw new Error('Failed to check payment status');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setPaymentError('Error checking payment status. Please check your profile for order updates.');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    // Clear preserved filename when a new file is uploaded
    setFormData({ ...formData, file, preserved_filename: null });
  };

  const scrollToQuoteSection = () => {
    setActiveTab("upload");
    setTimeout(() => {
      const quoteSection = document.querySelector('[data-section="quote-interface"]');
      if (quoteSection) {
        quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleFooterLinkClick = (section) => {
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToQuoteAndSetTab = (tabName) => {
    setActiveTab(tabName);
    setTimeout(() => {
      const quoteSection = document.querySelector('[data-section="quote-interface"]');
      if (quoteSection) {
        quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const navigateToPage = (page) => {
    navigate(`/${page}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToProduct = (product) => {
    const slug = createProductSlug(product.manufacturer, product.name);
    navigate(`/product/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToProducts = () => {
    navigate('/products');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToCart = () => {
    navigate('/cart');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateHome = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that either a file is uploaded OR we have an existing_quote_id
    if (!formData.file && !formData.existing_quote_id) {
      alert('Please upload a 3D model file');
      return;
    }
    
    setLoadingQuote(true);
    
    try {
      const formDataObj = new FormData();
      
      // Only append file if a new one is uploaded
      if (formData.file) {
        formDataObj.append('file', formData.file);
      }
      
      // If editing an existing quote, include the quote_id
      if (formData.existing_quote_id) {
        formDataObj.append('existing_quote_id', formData.existing_quote_id);
      }
      
      formDataObj.append('material', formData.material);
      formDataObj.append('color', formData.color === 'Other' ? formData.customColor : formData.color);
      formDataObj.append('quality', formData.quality);
      formDataObj.append('strength', formData.strength);
      formDataObj.append('smoothing', formData.smoothing);
      formDataObj.append('contact_name', formData.contact_name);  // Add contact name
      formDataObj.append('contact_email', formData.contact_email);
      
      // For STEP files, OpenCASCADE converts to mm internally, so always send 'mm' to backend
      // The displayed units to user are the original units, but backend receives actual data format
      const unitsForBackend = detectedUnits.rawOutputInMm ? 'mm' : formData.units;
      formDataObj.append('units', unitsForBackend);
      
      formDataObj.append('country_code', formData.country_code);  // Add shipping destination
      if (formData.postal_code) formDataObj.append('postal_code', formData.postal_code);  // Add postal code if provided
      if (formData.notes) formDataObj.append('notes', formData.notes);
      formDataObj.append('quantity', formData.quantity);  // Add quantity for bulk order discount
      
      // Add dimensions for accurate shipping calculation (if available from 3D analysis)
      if (formData.analysisData && formData.analysisData.dimensions) {
        formDataObj.append('dimension_x', parseFloat(formData.analysisData.dimensions.x));
        formDataObj.append('dimension_y', parseFloat(formData.analysisData.dimensions.y));
        formDataObj.append('dimension_z', parseFloat(formData.analysisData.dimensions.z));
        console.log('Sending dimensions for shipping calculation (cm):', {
          x: parseFloat(formData.analysisData.dimensions.x),
          y: parseFloat(formData.analysisData.dimensions.y),
          z: parseFloat(formData.analysisData.dimensions.z)
        });
      }

      const response = await fetch(`${API}/quote/3d-print`, {
        method: 'POST',
        body: formDataObj,
      });

      if (response.ok) {
        const result = await response.json();
        setQuoteData(result);
        setActiveTab("quote");
        setOrderSuccess(null); // Clear any previous order success messages
        
        // Track quote submission in Google Analytics
        try {
          trackQuoteSubmission({
            material: formData.material,
            quality: formData.quality,
            estimated_price: result.final_price
          });
        } catch (analyticsError) {
          console.warn('Analytics tracking failed:', analyticsError);
        }
        
        // Scroll to the instant quote section to show results
        setTimeout(() => {
          const quoteSection = document.querySelector('#instant-quote');
          if (quoteSection) {
            quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Quote submission failed:', errorData);
        alert('Error submitting quote request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      alert('Error submitting quote request. Please try again.');
    } finally {
      setLoadingQuote(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (!quoteData) {
      alert('No quote data available');
      return;
    }

    setLoadingOrder(true);
    
    try {
      // Get filename from formData (the uploaded file name)
      const filename = formData.file ? formData.file.name : quoteData.filename || 'Custom 3D Model';
      
      // Format description with all print settings from formData (user selections)
      const description = [
        `📄 File: ${filename}`,
        `🎨 Material: ${formData.material} - ${formData.color === 'Other' ? formData.customColor : formData.color}`,
        `⚙️ Quality: ${formData.quality}`,
        `💪 Strength: ${formData.strength}`,
        `✨ Smoothing: ${formData.smoothing}`
      ].join('\n');
      
      const cartItem = {
        id: `quote-${quoteData.quote_id}`, // Unique ID for cart item
        quote_id: quoteData.quote_id,
        type: 'print_quote',
        name: `Print Order - Quote ${quoteData.quote_id.substring(0, 8)}`,
        description: description,
        price: quoteData.price_breakdown.print_cost + quoteData.price_breakdown.smoothing_cost, // Price without shipping
        image: 'https://img.icons8.com/color/150/000000/3d-printer.png', // 3D printer icon
        image_url: 'https://img.icons8.com/color/150/000000/3d-printer.png', // Cart uses image_url
        category: 'Custom 3D Print',
        manufacturer: 'On-Demand Print',
        in_stock: true,
        quantity: 1, // Default quantity
        // Store all quote data for order creation
        quote_data: {
          quote_id: quoteData.quote_id,
          filename: filename,
          material: formData.material,
          color: formData.color === 'Other' ? formData.customColor : formData.color,
          quality: formData.quality,
          strength: formData.strength,
          smoothing: formData.smoothing,
          volume: quoteData.volume,
          surface_area: quoteData.surface_area,
          complexity_score: quoteData.complexity_score,
          price_breakdown: quoteData.price_breakdown,
          estimated_time: quoteData.estimated_time,
          delivery_weeks: quoteData.delivery_weeks,
          production_time: quoteData.production_time,
          contact_email: formData.contact_email,
          units: formData.units,
          shipping_country_code: formData.country_code
        }
      };
      
      // Add to cart
      addToCart(cartItem);
      
      // Show success message and redirect to cart
      alert(`✓ Quote added to cart!\n\nYou can now proceed to checkout or continue shopping.`);
      
      // Navigate to cart with scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate('/cart');
      
    } catch (error) {
      console.error('Error adding quote to cart:', error);
      alert('Error adding quote to cart. Please try again.');
    } finally {
      setLoadingOrder(false);
    }
  };

  const handlePayment = async (orderId) => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    setLoadingPayment(true);
    setPaymentError(null);

    try {
      // Get Firebase ID token
      const idToken = await getIdToken();
      if (!idToken) {
        setPaymentError('Authentication error. Please log in again.');
        setShowLoginModal(true);
        return;
      }

      // Get current URL for success and cancel URLs
      const currentUrl = window.location.origin + window.location.pathname;
      const successUrl = `${currentUrl}?payment=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}?payment=cancelled`;

      // Get backend URL
      const backendUrl = BACKEND_URL;
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      const response = await fetch(`${apiUrl}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include',
        body: JSON.stringify({
          order_id: orderId,
          success_url: successUrl,
          cancel_url: cancelUrl
        }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Redirect to Stripe checkout
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        const errorData = await response.json();
        if (response.status === 401) {
          setShowLoginModal(true);
          setPaymentError('Please log in to proceed with payment');
        } else {
          setPaymentError(`Payment error: ${errorData.detail || 'Please try again'}`);
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError('Error creating payment. Please try again.');
    } finally {
      setLoadingPayment(false);
    }
  };

  const handleServiceRequest = async (serviceType, formData, file = null) => {
    try {
      let endpoint;
      let body;
      let headers = {};

      if (serviceType === "2D to 3D Conversion" || serviceType === "Product Optimization") {
        if (!file) {
          alert('File upload is required for this service');
          return;
        }
        endpoint = `${API}/request/service-with-file`;
        body = new FormData();
        body.append('service_type', serviceType);
        body.append('contact_name', formData.name);
        body.append('contact_email', formData.email);
        body.append('description', formData.description);
        body.append('file', file);
      } else if (serviceType === "Design Service") {
        endpoint = `${API}/request/service-optional-file`;
        body = new FormData();
        body.append('service_type', serviceType);
        body.append('contact_name', formData.name);
        body.append('contact_email', formData.email);
        body.append('description', formData.description);
        if (file) {
          body.append('file', file);
        }
      } else {
        endpoint = `${API}/request/service`;
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({
          service_type: serviceType,
          contact_name: formData.name,
          contact_email: formData.email,
          description: formData.description,
          files_attached: false
        });
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: body,
      });

      if (response.ok) {
        // Track service request in Google Analytics
        trackServiceRequest(serviceType);
        
        alert('Service request submitted successfully! We will contact you within 24 hours.');
      } else {
        alert('Error submitting service request');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error submitting service request');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Clean Ambiq Style */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            {/* Logo with minimum width */}
            <div className="flex items-center flex-shrink-0">
              <img 
                src="/logo-nav.png" 
                alt="Create 3D Works Logo" 
                className="h-10 sm:h-12 w-auto object-contain"
                style={{ minWidth: '120px' }}
              />
            </div>

            {/* Spacer matching menu item spacing */}
            <div className="w-8 flex-shrink-0 hidden lg:block"></div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8 items-center">
              <button onClick={() => navigateToPage("services")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Services</button>
              <button onClick={() => navigateToPage("products")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Shop</button>
              <button onClick={() => navigateToPage("marketplace")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Designs</button>
              <button onClick={() => navigateToPage("about")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">About</button>
              <button onClick={() => navigateToPage("contact")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Contact</button>
            </nav>

            {/* Right-aligned Cart and Auth buttons for Desktop */}
            <div className="hidden lg:flex items-center space-x-4 ml-auto">
              {/* Launch Button - Primary CTA */}
              <a
                href="/get-started"
                className="bg-[#0007b5] hover:bg-[#000690] text-white px-5 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 shadow-md shadow-blue-500/20"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_801805a1-56d6-4a1b-9dc4-a3c271402139/artifacts/2uma28ub_Asset%205%401x.png" 
                  alt="C3D Logo" 
                  className="w-5 h-5 object-contain"
                />
                <span>Launch</span>
              </a>

              {/* Shopping Cart */}
              <button 
                onClick={() => navigateToPage("cart")}
                className="relative flex items-center space-x-2 font-medium text-slate-600 hover:text-[#0007b5] transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#0007b5] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
                <span className="hidden sm:inline">Cart</span>
              </button>
              
              {/* Firebase Authentication */}
              {loading ? (
                <div className="flex items-center space-x-2 font-medium text-slate-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0007b5]"></div>
                  <span>Loading...</span>
                </div>
              ) : isAuthenticated ? (
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => navigateToPage("profile")}
                    className="flex items-center space-x-2 font-medium text-slate-600 hover:text-[#0007b5] transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span>{user?.name || 'Profile'}</span>
                  </button>
                  <button 
                    onClick={logout}
                    className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors text-sm"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => setShowLoginModal(true)}
                  className="border-2 border-[#0007b5] text-[#0007b5] hover:bg-[#0007b5] hover:text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Sign In</span>
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden items-center gap-3 ml-auto">
              {/* Launch button for mobile */}
              <a 
                href="/get-started"
                className="bg-[#0007b5] hover:bg-[#000690] text-white px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center space-x-1.5 text-sm shadow-md shadow-blue-500/20"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_801805a1-56d6-4a1b-9dc4-a3c271402139/artifacts/2uma28ub_Asset%205%401x.png" 
                  alt="C3D Logo" 
                  className="w-4 h-4 object-contain"
                />
                <span>Launch</span>
              </a>

              {/* Cart for mobile */}
              <button 
                onClick={() => navigateToPage("cart")}
                className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ShoppingCart className="w-6 h-6 text-slate-600" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#0007b5] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                    {cart.length}
                  </span>
                )}
              </button>

              {/* Hamburger menu button */}
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-slate-600" />
                ) : (
                  <div className="space-y-1.5">
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu Panel */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-slate-100 pt-4">
              <nav className="flex flex-col space-y-4">
                <button onClick={() => {navigateToPage("services"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Services</button>
                <button onClick={() => {navigateToPage("products"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Shop</button>
                <button onClick={() => {navigateToPage("marketplace"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Designs</button>
                <button onClick={() => {navigateToPage("about"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">About</button>
                <button onClick={() => {navigateToPage("contact"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Contact</button>

                <div className="border-t border-slate-200 my-2"></div>

                {isAuthenticated ? (
                  <>
                    <button onClick={() => {navigateToPage("profile"); setMobileMenuOpen(false);}} className="text-left flex items-center space-x-2 py-2 text-slate-600 hover:text-[#0007b5] transition-colors">
                      <User className="w-4 h-4" />
                      <span>{user?.name || 'Profile'}</span>
                    </button>
                    <button onClick={async () => {await logout(); setMobileMenuOpen(false); navigateToPage("home");}} className="text-left py-2 text-slate-600 hover:text-[#0007b5] transition-colors">Sign Out</button>
                  </>
                ) : (
                  <button 
                    onClick={() => {setShowLoginModal(true); setMobileMenuOpen(false);}}
                    className="bg-[#0007b5] hover:bg-[#000690] text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 w-full"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Sign In</span>
                  </button>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Global Payment Status Messages */}
      {paymentStatus === 'success' && (
        <div className="bg-brand-green-50 border-l-4 border-brand-green-500 p-4 mx-4 mt-4 rounded-r-lg">
          <div className="flex items-center">
            <Check className="w-6 h-6 text-brand-green-500 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-brand-green-800">Payment Successful! 🎉</h3>
              <p className="text-brand-green-700">Your payment has been processed successfully. Your order is now confirmed and will begin production soon.</p>
              <div className="mt-3 space-x-4">
                <button
                  onClick={() => navigateToPage("profile")}
                  className="bg-brand-green-600 text-white px-4 py-2 rounded-lg hover:bg-brand-green-700 transition-colors text-sm font-medium"
                >
                  View Order Status
                </button>
                <button
                  onClick={() => setPaymentStatus(null)}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {paymentStatus === 'checking' && (
        <div className="bg-brand-blue-50 border-l-4 border-brand-blue-500 p-4 mx-4 mt-4 rounded-r-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-blue-600 mr-3"></div>
            <div>
              <h3 className="text-lg font-semibold text-brand-blue-800">Verifying Payment...</h3>
              <p className="text-brand-blue-700">Please wait while we confirm your payment with Stripe.</p>
            </div>
          </div>
        </div>
      )}

      {paymentError && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 mt-4 rounded-r-lg">
          <div className="flex items-center">
            <span className="text-red-500 text-xl mr-3">❌</span>
            <div>
              <h3 className="text-lg font-semibold text-red-800">Payment Issue</h3>
              <p className="text-red-700">{paymentError}</p>
              <button
                onClick={() => setPaymentError(null)}
                className="mt-2 bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors text-sm"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section - Video Background with Swiss Tech Polish */}
      <section className="relative min-h-screen overflow-hidden bg-slate-900">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectFit: 'cover' }}
          >
            <source src="/C3D_Banner_Video_2.mp4" type="video/mp4" />
          </video>
          
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-slate-900/75"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-32">
          <div className="text-center">
            {/* Clean Typography - Swiss Tech Style */}
            <div className="mb-10">
              <p className="text-blue-300 text-sm font-medium tracking-widest uppercase mb-6">Professional 3D Printing Services</p>
              <h1 className="font-heading font-bold tracking-tight leading-none">
                <span className="block text-5xl md:text-7xl lg:text-8xl text-white">3D Printing</span>
                <span className="block text-4xl md:text-6xl lg:text-7xl mt-4 text-blue-400">Tailored to Your Needs</span>
              </h1>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-8 mt-8 max-w-4xl mx-auto">
                <span className="text-white">Let us</span>{' '}
                <span className="text-blue-400">design your parts</span>{' '}
                <span className="text-white">or</span>{' '}
                <span className="text-blue-400">print yours.</span>
              </h2>
            </div>

            {/* Clean Description */}
            <p className="text-lg md:text-xl text-slate-300 mb-14 max-w-3xl mx-auto leading-relaxed">
              Use our <span className="text-white font-semibold">instant quote tool</span> to <span className="text-white font-semibold">print your own parts</span> to your specs. Don&apos;t have a 3D model? <span className="text-white font-semibold">Let us design your part</span> from scratch, from a hand sketch, or convert a 2D drawing.
            </p>

            {/* Clean CTA Buttons - Swiss Tech Style */}
            <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
              <Button 
                size="lg" 
                className="bg-[#0007b5] hover:bg-[#000690] text-white px-10 py-4 text-lg font-semibold rounded-xl shadow-lg shadow-blue-900/30 hover:-translate-y-0.5 transition-all duration-300"
                onClick={scrollToQuoteSection}
              >
                Get Instant Quote
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-white/80 text-white hover:bg-white hover:text-[#0007b5] px-10 py-4 text-lg font-semibold rounded-xl transition-all duration-300"
                onClick={() => navigateToPage("marketplace")}
              >
                Explore CAD Library
                <FileText className="ml-2 w-5 h-5" />
              </Button>
              
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-white/80 text-white hover:bg-white hover:text-[#0007b5] px-10 py-4 text-lg font-semibold rounded-xl transition-all duration-300"
                onClick={() => navigateToPage("products")}
              >
                Explore Products
                <Package className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Trust Indicators - Swiss Tech Style */}
            <div className="mt-20 pt-10 border-t border-slate-700/50">
              <p className="text-slate-500 text-xs font-medium tracking-[0.2em] uppercase mb-6">Trusted by</p>
              <div className="flex items-center justify-center space-x-10 text-slate-400 text-sm font-semibold tracking-wide">
                <span>GOVERNMENT</span>
                <span className="text-slate-600">•</span>
                <span>UNIVERSITIES</span>
                <span className="text-slate-600">•</span>
                <span>BUSINESS</span>
              </div>
            </div>
          </div>
        </div>

        {/* Clean scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-5 h-5 text-slate-400 rotate-90" />
        </div>
      </section>

      {/* Services Overview - Swiss Tech Design */}
      <section className="py-28 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Header - Swiss Tech Style */}
          <div className="flex items-center mb-20">
            <div className="text-8xl font-bold text-slate-100 mr-8 font-heading">01</div>
            <div>
              <p className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-3">Engineering Expertise</p>
              <h3 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight font-heading">
                Flexible Solutions
              </h3>
            </div>
          </div>
          
          {/* Services Grid - Two Row Layout */}
          <div className="space-y-8 mb-20" ref={flexibleSolutionsRef}>
            {/* First Row - 2 Larger Cards (Primary Services) */}
            <div className="grid md:grid-cols-2 gap-8">
            <div 
              data-testid="flex-solution-card-on-demand"
              className={`group cursor-pointer h-full ${flexibleVisible ? 'animate-slide-in-right' : 'opacity-0'}`}
              style={flexibleVisible ? { animationDelay: '0ms', animationFillMode: 'both' } : undefined}
              onClick={() => {
                setActiveTab("upload");
                
                setTimeout(() => {
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  
                  if (quoteSection) {
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100;
                    
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col group-hover:border-[#0007b5]/20">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Upload className="w-7 h-7 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3">On Demand Printing</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Automated intelligent analysis of your models with a wide range of material selections and printing settings based on your use and requirements. Upload your model and get an instant quote.
                </p>
              </div>
            </div>

            <div 
              data-testid="flex-solution-card-design"
              className={`group cursor-pointer h-full ${flexibleVisible ? 'animate-slide-in-right' : 'opacity-0'}`}
              style={flexibleVisible ? { animationDelay: '300ms', animationFillMode: 'both' } : undefined}
              onClick={() => {
                setActiveTab("design");
                setTimeout(() => {
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  if (quoteSection) {
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100;
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col group-hover:border-[#0007b5]/20">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <DraftingCompass className="w-7 h-7 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3">Design Services</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Complete design-to-production services with engineering expertise in functional, cosmetic, and load-bearing applications.
                </p>
              </div>
            </div>
            </div>

            {/* Second Row - 3 Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div 
              data-testid="flex-solution-card-convert"
              className={`group cursor-pointer h-full ${flexibleVisible ? 'animate-slide-in-left' : 'opacity-0'}`}
              style={flexibleVisible ? { animationDelay: '600ms', animationFillMode: 'both' } : undefined}
              onClick={() => {
                setActiveTab("convert");
                
                setTimeout(() => {
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  
                  if (quoteSection) {
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100;
                    
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col group-hover:border-[#0007b5]/20">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Layers className="w-7 h-7 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3">2D to 3D Conversion</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Expert engineering services to transform sketches or technical drawings into optimized 3D models ready for production.
                </p>
              </div>
            </div>
            <div 
              data-testid="flex-solution-card-optimize"
              className={`group cursor-pointer h-full ${flexibleVisible ? 'animate-slide-in-left' : 'opacity-0'}`}
              style={flexibleVisible ? { animationDelay: '900ms', animationFillMode: 'both' } : undefined}
              onClick={() => {
                setActiveTab("optimize");
                setTimeout(() => {
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  if (quoteSection) {
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100;
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col group-hover:border-[#0007b5]/20">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Settings className="w-7 h-7 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3">Product Optimization</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Engineering analysis and optimization of your existing 3D models for improved performance, durability, and manufacturing efficiency.
                </p>
              </div>
            </div>

            <div 
              data-testid="flex-solution-card-renders"
              className={`group cursor-pointer h-full ${flexibleVisible ? 'animate-slide-in-left' : 'opacity-0'}`}
              style={flexibleVisible ? { animationDelay: '1200ms', animationFillMode: 'both' } : undefined}
              onClick={() => {
                setActiveTab("renders");
                setTimeout(() => {
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  if (quoteSection) {
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100;
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col group-hover:border-[#0007b5]/20">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Eye className="w-7 h-7 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3">Product Renders</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Realistic renders of 3D printed products, including layer visualization or post processing and smoothing, for planning, marketing, or product listing pictures.
                </p>
              </div>
            </div>
            </div>
          </div>

          {/* Feature Highlights */}
          <div className="text-center">
            <p className="text-slate-600 text-lg max-w-4xl mx-auto leading-relaxed">
              Engineering-driven approach with experience across prototype, cosmetic, functional, load-bearing, 
              and wrapped components. Flexible solutions for every application.
            </p>
          </div>
        </div>
      </section>

      {/* Technology Section - Swiss Tech Design */}
      <section id="about" className="py-28 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Header - Swiss Tech Style */}
          <div className="flex items-center mb-20">
            <div className="text-8xl font-bold text-slate-200 mr-8 font-heading">02</div>
            <div>
              <p className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-3">Engineering Expertise</p>
              <h3 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight font-heading">
                Proven Experience
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            {/* Left Content */}
            <div className="space-y-10">
              <div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Engineering-Driven Solutions</h4>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Created by a seasoned &quot;skunkworks team&quot; with over 8 combined years of hands-on 3D printing experience and designing end-use parts specifically for 3D printing. We understand the technical challenges and provide solutions that work.
                </p>
              </div>
              
              <div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Complete Application Range</h4>
                <p className="text-lg text-slate-600 leading-relaxed">
                  From prototype testing to production-grade components. Experience with cosmetic parts, functional assemblies, 
                  load-bearing structures, smoothed surfaces, painted finishes, and wrapped components.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-8 pt-8">
                <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
                  <div className="text-2xl font-bold text-slate-900 mb-1">Custom</div>
                  <div className="text-sm text-slate-600 font-medium">Design Parts</div>
                </div>
                <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
                  <div className="text-2xl font-bold text-slate-900 mb-1">3,000+ lb</div>
                  <div className="text-sm text-slate-600 font-medium">Filament Printed</div>
                </div>
                <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
                  <div className="text-2xl font-bold text-slate-900 mb-1">Extensive Range</div>
                  <div className="text-sm text-slate-600 font-medium">of Applications</div>
                </div>
              </div>
            </div>

            {/* Right Content - Clean Image */}
            <div className="relative">
              <img 
                src="/home_printed_components_2.png"
                alt="Precision Mechanical Components"
                className="rounded-2xl shadow-2xl w-full h-auto object-contain"
              />
            </div>
          </div>

        </div>
      </section>

      {/* Start Your Project CTA Section */}
      {/* 
        SAVED PREVIOUS BACKGROUND STYLE (can revert if needed):
        <section className="py-24 bg-gradient-to-br from-slate-900 via-[#0007b5] to-slate-900 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 -left-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl"></div>
          </div>
      */}
      <section className="py-24 bg-gradient-to-br from-[#00068a] via-[#000cb8] to-[#0015e6] relative overflow-hidden" data-section="quote-interface">
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              <span className="text-white/90 text-sm font-medium">Ready to bring your ideas to life?</span>
            </div>
            
            {/* Main Heading */}
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
              Start Your Project
            </h2>
            
            {/* Subheading */}
            <p className="text-xl text-blue-100/80 max-w-2xl mx-auto mb-12">
              Get an instant print-on-demand quote, or request our services at any stage of your project from concept to finished product.
            </p>
            
            {/* Service Cards Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
              <a href="/get-started?service=on-demand-3d-print" className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-1">Instant Quote</h3>
                <p className="text-sm text-blue-200/60">Upload & price</p>
              </a>
              
              <a href="/get-started?tab=design" className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                  <DraftingCompass className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-1">Design</h3>
                <p className="text-sm text-blue-200/60">Custom models</p>
              </a>
              
              <a href="/get-started?tab=convert" className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                  <Layers className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-1">2D to 3D</h3>
                <p className="text-sm text-blue-200/60">Convert files</p>
              </a>
              
              <a href="/get-started?tab=optimize" className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-1">Optimize</h3>
                <p className="text-sm text-blue-200/60">Better prints</p>
              </a>
              
              <a href="/get-started?tab=renders" className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-1">Renders</h3>
                <p className="text-sm text-blue-200/60">Product visuals</p>
              </a>
            </div>
            
            {/* Main CTA Button */}
            <a 
              href="/get-started" 
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-[#0007b5] rounded-xl font-semibold text-lg hover:bg-blue-50 transition-all duration-300 shadow-2xl shadow-black/20 hover:shadow-blue-500/20 hover:scale-105"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5" />
            </a>
            
            {/* Trust indicators */}
            <div className="flex items-center justify-center gap-8 mt-12 text-blue-200/60 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Instant Quotes</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Fast Turnaround</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Expert Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 03 - Build Your Own Products */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Number - Ambiq Style */}
          <div className="flex items-center mb-16">
            <div className="text-8xl font-black text-slate-100 mr-8">03</div>
            <div>
              <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">EXPERT SUPPORT</div>
              <h3 className="text-4xl md:text-5xl font-black text-slate-900">
                Build Your Own Products
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Professional Equipment & Support</h4>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  Ready to bring 3D printing in-house? We offer a broad range of printers and filaments 
                  with comprehensive support to get you started. Every purchase includes 1 month of free 
                  expert consulting to fuel your success.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Free 1-Month Support:</strong> Direct access to our engineering team for troubleshooting and optimization</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Print Quality Consulting:</strong> Help with failing prints, layer adhesion, and surface finish issues</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Material Guidance:</strong> Recommendations for your specific applications and use cases</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Setup & Calibration:</strong> Printer setup guidance and calibration support</p>
                  </div>
                </div>
              </div>
              
              <Button 
                className="bg-[#0007b5] hover:bg-[#000690] text-white px-12 py-4 text-lg font-semibold rounded-lg shadow-xl transition-all duration-300"
                onClick={() => navigateToPage("products")}
              >
                Shop Now
                <Package className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Right Content - Visual */}
            <div className="relative">
              <img 
                src="/images/hero-design-marketplace.png"
                alt="Professional 3D Printing Setup"
                className="rounded-2xl shadow-2xl w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Section 04 - CAD Library / Marketplace */}
      <section className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Number - Ambiq Style */}
          <div className="flex items-center mb-16">
            <div className="text-8xl font-black text-slate-100 mr-8">04</div>
            <div>
              <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">DESIGNER MARKETPLACE</div>
              <h3 className="text-4xl md:text-5xl font-black text-slate-900">
                Explore CAD Library
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content - Features Grid */}
            <div className="space-y-6">
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                Access our 3D CAD library of professional 3D models designed specifically for 3D printing. 
                Download files to use instantly or order it printed directly from the listing.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <Combine className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">Print-Ready Files</h4>
                  <p className="text-slate-600 text-sm">
                    Download optimized CAD files ready for immediate 3D printing with any printer.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">Expert Designers</h4>
                  <p className="text-slate-600 text-sm">
                    Models created by experienced engineers and designers who understand 3D printing.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <Package className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">Print on Demand</h4>
                  <p className="text-slate-600 text-sm">
                    Order prints directly from the marketplace with material and finish options.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <Rotate3d className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">3D Preview</h4>
                  <p className="text-slate-600 text-sm">
                    Interactive 3D preview of every model before download or purchase.
                  </p>
                </div>
              </div>

              <div className="pt-6">
                <Button 
                  className="bg-[#0007b5] hover:bg-[#000690] text-white px-12 py-4 text-lg font-semibold rounded-lg shadow-xl transition-all duration-300"
                  onClick={() => navigateToPage("marketplace")}
                >
                  Browse CAD Library
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Right Content - Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-slate-100 rounded-2xl p-8 shadow-xl">
                <div className="space-y-4">
                  {/* Mock design card */}
                  <div className="bg-white rounded-xl p-6 shadow-md border border-slate-200">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h5 className="font-bold text-slate-900 mb-1">Professional CAD Models</h5>
                        <p className="text-sm text-slate-500">Available licenses for personal or commercial use</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-[#0007b5]" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Multiple Formats</span>
                      <span className="font-bold text-[#0007b5]">STL • 3MF • STEP</span>
                    </div>
                  </div>

                  {/* Mock stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-4 text-center shadow-md">
                      <div className="text-lg font-black text-[#0007b5] mb-1">Free & Premium</div>
                      <div className="text-xs text-slate-600">Models Available</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center shadow-md">
                      <div className="text-lg font-black text-[#0007b5] mb-1">Parts & Assemblies</div>
                      <div className="text-xs text-slate-600">Various Applications and Use Cases</div>
                    </div>
                  </div>

                  {/* Search by Category */}
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <h6 className="font-bold text-slate-900 mb-3 text-sm">Search by Category</h6>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Organization</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Toys</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Decorative</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Models</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Sim Racing</span>
                      <span className="px-3 py-1 bg-gradient-to-r from-[#0007b5] to-blue-600 text-white rounded-full text-xs font-medium">and more →</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-blue-100 rounded-2xl opacity-20"></div>
            </div>
          </div>

          {/* Additional Info Banner */}
          <div className="mt-16 bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl font-black text-slate-900 mb-2">Instant Download</div>
                <p className="text-slate-600">Get files immediately after purchase</p>
              </div>
              <div>
                <div className="text-3xl font-black text-slate-900 mb-2">Quality Verified</div>
                <p className="text-slate-600">All models tested and print-ready</p>
              </div>
              <div>
                <div className="text-3xl font-black text-slate-900 mb-2">Designer Support</div>
                <p className="text-slate-600">Direct contact with model creators</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Contact Section - Clean Style */}
      <section id="contact" className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          <div className="text-center mb-16">
            <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">GET IN TOUCH</div>
            <h3 className="text-4xl md:text-5xl font-black text-slate-900">
              Contact Us
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 max-w-3xl mx-auto">
            <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                  <Mail className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Email Us</h4>
                <p className="text-slate-600 text-lg">hello@create3dworks.com</p>
                <p className="text-slate-500 text-sm mt-1">Same Day Response</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Visit Us</h4>
                <p className="text-slate-600 text-lg">590 Portland St - Unit 117<br />Dartmouth, NS B2W 6B7<br />Canada</p>
                <p className="text-slate-500 text-sm mt-1">Showroom hours coming soon!</p>
              </CardContent>
            </Card>
          </div>

          {/* CTA Section */}
          <section className="text-center bg-gradient-to-r from-slate-900 to-blue-900 rounded-3xl p-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Experience the difference that engineering expertise makes. Get an instant quote 
              or contact us to discuss your project requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={scrollToQuoteSection}
                className="bg-white text-[#0007b5] hover:bg-blue-50 px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                Get Instant Quote
              </button>
              <button
                onClick={() => {
                  navigate('/contact');
                  setTimeout(() => {
                    const helpSection = document.querySelector('#here-to-help');
                    if (helpSection) {
                      const yOffset = -80;
                      const y = helpSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                      window.scrollTo({ top: y, behavior: 'smooth' });
                    }
                  }, 100);
                }}
                className="border border-blue-200 text-white hover:bg-blue-800 px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                Contact Us
              </button>
            </div>
          </section>
        </div>
      </section>

      {/* Footer - Clean Ambiq Style */}
      <Footer />

      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
    </div>
  );
};

// Service Request Form Component
const ServiceRequestForm = ({ title, description, serviceType, fileRequired, onSubmit, inline = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward
  const [legendOpen, setLegendOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
    file: null
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFormData({ ...formData, file });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (fileRequired && !formData.file) {
      alert('File upload is required for this service');
      return;
    }
    onSubmit(serviceType, formData, formData.file);
    setFormData({ name: "", email: "", description: "", file: null });
    setCurrentStep(0);
  };

  const goToStep = (step) => {
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
    setLegendOpen(false);
  };

  const nextStep = () => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, 1));
  };

  const prevStep = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const getServiceIcon = () => {
    switch(serviceType) {
      case "2D to 3D Conversion": return Layers;
      case "Design Service": return DraftingCompass;
      case "Product Optimization": return Settings;
      case "Product Renders": return Eye;
      default: return FileText;
    }
  };
  const ServiceIcon = getServiceIcon();

  const steps = [
    { title: "Project Details", icon: FileText },
    { title: "Contact Info", icon: User }
  ];

  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // The form content (sidebar + form)
  const formContent = (
    <>
      {/* Progress bar for inline mode */}
      {inline && (
        <div className="px-4 md:px-8 pb-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">Step {currentStep + 1} of {totalSteps}</span>
          </div>
          <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
      <div className="flex flex-col lg:flex-row">
        {/* Legend Sidebar */}
        <div className={`
          ${legendOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
          lg:w-64 lg:min-h-full overflow-hidden transition-all duration-300 ease-out
          bg-gradient-to-b from-[#0007b5] via-[#0010d9] to-blue-600
        `}>
          <div className="p-4 lg:p-6">
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Steps</span>
              <button
                type="button"
                onClick={() => setLegendOpen(false)}
                className="lg:hidden p-1 rounded hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4 text-white/80" />
              </button>
            </div>
            <nav className="space-y-2">
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = currentStep === index;
                const isCompleted = currentStep > index;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => goToStep(index)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                      isActive 
                        ? 'bg-white/20 text-white shadow-lg' 
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-white text-[#0007b5]' : isCompleted ? 'bg-white/30 text-white' : 'bg-white/10 text-white/60'
                    }`}>
                      {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-white/50">Step {index + 1}</p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 p-4 md:p-8">
          <form onSubmit={handleSubmit}>
            {/* Fixed File Upload Section */}
            <div className="mb-6 pb-6 border-b border-slate-200">
              <div className={`relative border-2 border-dashed rounded-xl p-4 md:p-6 transition-all duration-300 ${
                formData.file ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-300 hover:border-[#0007b5] bg-slate-50'
              }`}>
                {formData.file ? (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm md:text-base">{formData.file.name}</p>
                        <p className="text-xs text-slate-500">
                          {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => document.getElementById(`service-file-${serviceType.replace(/\s/g, '-')}`).click()}
                      className="px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => document.getElementById(`service-file-${serviceType.replace(/\s/g, '-')}`).click()}
                      className="inline-flex items-center justify-center px-5 py-2.5 bg-[#0007b5] text-white rounded-xl hover:bg-[#0007b5]/90 transition-colors font-semibold shadow-lg shadow-[#0007b5]/20 text-sm md:text-base"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {fileRequired ? "Upload File" : "Upload (Optional)"}
                    </button>
                    <p className="text-slate-500 mt-2 text-xs md:text-sm">
                      CAD files, 3D models, images, PDFs
                    </p>
                  </div>
                )}
                <Input
                  id={`service-file-${serviceType.replace(/\s/g, '-')}`}
                  type="file"
                  accept=".pdf,.dwg,.dxf,.stl,.obj,.3mf,.ply,.amf,.step,.stp,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  required={fileRequired}
                  className="hidden"
                />
              </div>
              <div className="flex items-start gap-2 mt-3 text-xs text-slate-500">
                <Lock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <p>Your files are protected and confidential.</p>
              </div>
            </div>

            {/* Animated Step Content */}
            <div className="relative overflow-hidden min-h-[200px]">
              {/* Step 0: Project Description */}
              <div 
                className={`transition-all duration-400 ease-out ${
                  currentStep === 0 
                    ? 'opacity-100 translate-x-0' 
                    : direction > 0 
                      ? 'opacity-0 -translate-x-full absolute inset-0' 
                      : 'opacity-0 translate-x-full absolute inset-0'
                }`}
              >
                <div className="space-y-2">
                  <Label htmlFor={`desc-${serviceType.replace(/\s/g, '-')}`} className="text-base font-semibold text-slate-900">
                    Project Description
                  </Label>
                  <p className="text-sm text-slate-500 mb-3">Tell us about your project requirements</p>
                  <Textarea
                    id={`desc-${serviceType.replace(/\s/g, '-')}`}
                    placeholder="Describe your project in detail..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    className="resize-none min-h-[120px]"
                    rows={5}
                  />
                </div>
              </div>

              {/* Step 1: Contact Info */}
              <div 
                className={`transition-all duration-400 ease-out ${
                  currentStep === 1 
                    ? 'opacity-100 translate-x-0' 
                    : direction > 0 
                      ? 'opacity-0 translate-x-full absolute inset-0' 
                      : 'opacity-0 -translate-x-full absolute inset-0'
                }`}
              >
                <div className="space-y-6">
                  <div>
                    <Label htmlFor={`name-${serviceType.replace(/\s/g, '-')}`} className="text-base font-semibold text-slate-900">
                      Your Name
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">So we know who to contact</p>
                    <input
                      id={`name-${serviceType.replace(/\s/g, '-')}`}
                      type="text"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl transition-all duration-300 focus:border-[#0007b5] focus:outline-none focus:ring-0 hover:border-slate-300 placeholder-slate-400"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`email-${serviceType.replace(/\s/g, '-')}`} className="text-base font-semibold text-slate-900">
                      Email Address
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">Where we'll send updates</p>
                    <input
                      id={`email-${serviceType.replace(/\s/g, '-')}`}
                      type="email"
                      placeholder="name@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl transition-all duration-300 focus:border-[#0007b5] focus:outline-none focus:ring-0 hover:border-slate-300 placeholder-slate-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
              <button
                type="button"
                onClick={prevStep}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-300 ${
                  currentStep === 0 
                    ? 'opacity-0 pointer-events-none' 
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>

              {currentStep < totalSteps - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#0007b5] text-white rounded-xl font-semibold hover:bg-[#000690] transition-all shadow-lg shadow-blue-500/20"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <Button 
                  type="submit" 
                  className="px-6 py-2.5 bg-[#0007b5] hover:bg-[#000690] text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/20"
                >
                  Submit Request
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );

  // If inline mode, return just the content
  if (inline) {
    return formContent;
  }

  // Otherwise, wrap in container with header
  return (
    <div className="bg-gradient-to-br from-white via-white to-blue-50/30 rounded-2xl shadow-xl border border-slate-200/60 overflow-hidden">
      <div className="relative px-4 md:px-8 py-6 border-b border-slate-100">
        <div className="absolute top-0 left-0 w-full h-1 bg-slate-200">
          <div 
            className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setLegendOpen(!legendOpen)}
              className="lg:hidden p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-[#0007b5] flex items-center justify-center shadow-lg shadow-blue-500/20">
              <ServiceIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold text-slate-900 tracking-tight">{title}</h2>
              <p className="text-xs md:text-sm text-slate-500 hidden sm:block">{description}</p>
            </div>
          </div>
          <div className="text-sm text-slate-500 font-medium hidden sm:block">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>
      </div>
      {formContent}
    </div>
  );
};

// Service Request Form with 3D Preview (for Optimize and Renders tabs)
const ServiceRequestFormWith3DPreview = ({ title, description, serviceType, onSubmit, inline = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [legendOpen, setLegendOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
    file: null
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFormData({ ...formData, file });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.file) {
      alert('File upload is required for this service');
      return;
    }
    onSubmit(serviceType, formData, formData.file);
    setFormData({ name: "", email: "", description: "", file: null });
    setCurrentStep(0);
  };

  const goToStep = (step) => {
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
    setLegendOpen(false);
  };

  const nextStep = () => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, 1));
  };

  const prevStep = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const getServiceIcon = () => {
    switch(serviceType) {
      case "Product Optimization": return Settings;
      case "Product Renders": return Eye;
      default: return FileText;
    }
  };
  const ServiceIcon = getServiceIcon();

  const steps = [
    { title: "Project Details", icon: FileText },
    { title: "Contact Info", icon: User }
  ];

  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // The form content (sidebar + form)
  const formContent = (
    <>
      {/* Progress bar for inline mode */}
      {inline && (
        <div className="px-4 md:px-8 pb-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">Step {currentStep + 1} of {totalSteps}</span>
          </div>
          <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
      <div className="flex flex-col lg:flex-row">
        {/* Legend Sidebar */}
        <div className={`
          ${legendOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
          lg:w-64 lg:min-h-full overflow-hidden transition-all duration-300 ease-out
          bg-gradient-to-b from-[#0007b5] via-[#0010d9] to-blue-600
        `}>
          <div className="p-4 lg:p-6">
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Steps</span>
              <button
                type="button"
                onClick={() => setLegendOpen(false)}
                className="lg:hidden p-1 rounded hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4 text-white/80" />
              </button>
            </div>
            <nav className="space-y-2">
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = currentStep === index;
                const isCompleted = currentStep > index;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => goToStep(index)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                      isActive 
                        ? 'bg-white/20 text-white shadow-lg' 
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-white text-[#0007b5]' : isCompleted ? 'bg-white/30 text-white' : 'bg-white/10 text-white/60'
                    }`}>
                      {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-white/50">Step {index + 1}</p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 p-4 md:p-8">
          <form onSubmit={handleSubmit}>
            {/* Fixed 3D Preview Section */}
            <div className="mb-6 pb-6 border-b border-slate-200">
              <div className="rounded-2xl overflow-hidden bg-slate-900 border border-slate-700">
                {formData.file ? (
                  <div className="p-3 md:p-4">
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-700 flex-wrap gap-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-[#0007b5] rounded-lg flex items-center justify-center">
                          <FileText className="h-4 w-4 md:h-5 md:w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs md:text-sm font-semibold text-white truncate max-w-[150px] md:max-w-xs">
                            {formData.file.name}
                          </p>
                          <p className="text-xs text-slate-400">
                            {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => document.getElementById(`3d-file-${serviceType.replace(/\s/g, '-')}`).click()}
                        className="px-3 py-1.5 bg-slate-800 border border-slate-600 text-white rounded-lg hover:bg-slate-700 text-xs md:text-sm font-medium transition-colors"
                      >
                        Change
                      </button>
                    </div>
                    <div className="min-h-[250px] md:min-h-[300px] rounded-xl overflow-hidden bg-gradient-to-b from-slate-800 to-slate-900">
                      <ThreeDPreview 
                        file={formData.file}
                        onAnalysis={(analysisData) => {
                          console.log('3D Analysis:', analysisData);
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="p-6 md:p-8 text-center">
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Boxes className="w-7 h-7 md:w-8 md:h-8 text-slate-500" />
                    </div>
                    <p className="text-slate-400 mb-4 text-sm">Upload your 3D model to see preview</p>
                    <button
                      type="button"
                      onClick={() => document.getElementById(`3d-file-${serviceType.replace(/\s/g, '-')}`).click()}
                      className="inline-flex items-center justify-center px-5 py-2.5 bg-[#0007b5] text-white rounded-xl hover:bg-[#0007b5]/90 transition-colors font-semibold shadow-lg shadow-[#0007b5]/20 text-sm"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Browse 3D Files
                    </button>
                    <p className="text-slate-500 mt-3 text-xs">
                      STL, OBJ, 3MF, STEP, PLY
                    </p>
                  </div>
                )}
                <Input
                  id={`3d-file-${serviceType.replace(/\s/g, '-')}`}
                  type="file"
                  accept=".stl,.obj,.3mf,.ply,.step,.stp"
                  onChange={handleFileUpload}
                  required
                  className="hidden"
                />
              </div>
              <div className="flex items-start gap-2 mt-3 text-xs text-slate-500">
                <Lock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <p>Your files are protected and confidential.</p>
              </div>
            </div>

            {/* Animated Step Content */}
            <div className="relative overflow-hidden min-h-[200px]">
              {/* Step 0: Project Description */}
              <div 
                className={`transition-all duration-400 ease-out ${
                  currentStep === 0 
                    ? 'opacity-100 translate-x-0' 
                    : direction > 0 
                      ? 'opacity-0 -translate-x-full absolute inset-0' 
                      : 'opacity-0 translate-x-full absolute inset-0'
                }`}
              >
                <div className="space-y-2">
                  <Label htmlFor={`desc-${serviceType.replace(/\s/g, '-')}`} className="text-base font-semibold text-slate-900">
                    Project Description
                  </Label>
                  <p className="text-sm text-slate-500 mb-3">Tell us about your project requirements</p>
                  <Textarea
                    id={`desc-${serviceType.replace(/\s/g, '-')}`}
                    placeholder="Describe your project in detail..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    className="resize-none min-h-[120px]"
                    rows={5}
                  />
                </div>
              </div>

              {/* Step 1: Contact Info */}
              <div 
                className={`transition-all duration-400 ease-out ${
                  currentStep === 1 
                    ? 'opacity-100 translate-x-0' 
                    : direction > 0 
                      ? 'opacity-0 translate-x-full absolute inset-0' 
                      : 'opacity-0 -translate-x-full absolute inset-0'
                }`}
              >
                <div className="space-y-6">
                  <div>
                    <Label htmlFor={`name-${serviceType.replace(/\s/g, '-')}`} className="text-base font-semibold text-slate-900">
                      Your Name
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">So we know who to contact</p>
                    <input
                      id={`name-${serviceType.replace(/\s/g, '-')}`}
                      type="text"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl transition-all duration-300 focus:border-[#0007b5] focus:outline-none focus:ring-0 hover:border-slate-300 placeholder-slate-400"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`email-${serviceType.replace(/\s/g, '-')}`} className="text-base font-semibold text-slate-900">
                      Email Address
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">Where we'll send updates</p>
                    <input
                      id={`email-${serviceType.replace(/\s/g, '-')}`}
                      type="email"
                      placeholder="name@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl transition-all duration-300 focus:border-[#0007b5] focus:outline-none focus:ring-0 hover:border-slate-300 placeholder-slate-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
              <button
                type="button"
                onClick={prevStep}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-300 ${
                  currentStep === 0 
                    ? 'opacity-0 pointer-events-none' 
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>

              {currentStep < totalSteps - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#0007b5] text-white rounded-xl font-semibold hover:bg-[#000690] transition-all shadow-lg shadow-blue-500/20"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <Button 
                  type="submit" 
                  className="px-6 py-2.5 bg-[#0007b5] hover:bg-[#000690] text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/20"
                >
                  Submit Request
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );

  // If inline mode, return just the content
  if (inline) {
    return formContent;
  }

  // Otherwise, wrap in container with header
  return (
    <div className="bg-gradient-to-br from-white via-white to-blue-50/30 rounded-2xl shadow-xl border border-slate-200/60 overflow-hidden">
      <div className="relative px-4 md:px-8 py-6 border-b border-slate-100">
        <div className="absolute top-0 left-0 w-full h-1 bg-slate-200">
          <div 
            className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setLegendOpen(!legendOpen)}
              className="lg:hidden p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-[#0007b5] flex items-center justify-center shadow-lg shadow-blue-500/20">
              <ServiceIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold text-slate-900 tracking-tight">{title}</h2>
              <p className="text-xs md:text-sm text-slate-500 hidden sm:block">{description}</p>
            </div>
          </div>
          <div className="text-sm text-slate-500 font-medium hidden sm:block">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>
      </div>
      {formContent}
    </div>
  );
};

// Design Service Form with expanded project details
const DesignServiceForm = ({ onSubmit, inline = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [legendOpen, setLegendOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
    file: null,
    application: "",
    applicationOther: "",
    configuration: "",
    printingRequirements: "",
    fitmentRequirements: "",
    timeline: "",
    timelineOther: "",
    optionalServices: []
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFormData({ ...formData, file });
  };

  const handleOptionalServiceChange = (service) => {
    setFormData(prev => ({
      ...prev,
      optionalServices: prev.optionalServices.includes(service)
        ? prev.optionalServices.filter(s => s !== service)
        : [...prev.optionalServices, service]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      application: formData.application === 'other' ? formData.applicationOther : formData.application,
      timeline: formData.timeline === 'other' ? formData.timelineOther : formData.timeline
    };
    onSubmit("Design Service", submitData, formData.file);
    setFormData({ 
      name: "", email: "", description: "", file: null,
      application: "", applicationOther: "", configuration: "", 
      printingRequirements: "", fitmentRequirements: "", 
      timeline: "", timelineOther: "", optionalServices: []
    });
    setCurrentStep(0);
  };

  const goToStep = (step) => {
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
    setLegendOpen(false);
  };

  const nextStep = () => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const applicationOptions = [
    { value: "auto-interior", label: "Auto Interior" },
    { value: "auto-suspension", label: "Auto Suspension" },
    { value: "auto-engine-bay", label: "Auto Engine Bay" },
    { value: "drone-chassis", label: "Drone Chassis" },
    { value: "drone-accessories", label: "Drone Accessories" },
    { value: "organization", label: "Organization" },
    { value: "decorative", label: "Decorative" },
    { value: "storage", label: "Storage" },
    { value: "sim-racing", label: "Sim Racing" },
    { value: "flight-sim", label: "Flight Sim" },
    { value: "other", label: "Other" }
  ];

  const configurationOptions = [
    { value: "individual", label: "Individual piece" },
    { value: "assembly", label: "Assembly" },
    { value: "not-sure", label: "Not sure" }
  ];

  const printingRequirementsOptions = [
    { value: "prototyping-only", label: "Prototyping only" },
    { value: "prototyping-and-production", label: "Prototyping and final production" }
  ];

  const fitmentOptions = [
    { value: "no-fitment", label: "No fitment required with other parts" },
    { value: "provide-dimensions", label: "I will provide dimensions" },
    { value: "provide-3d-scan", label: "I will provide 3D scan(s)" },
    { value: "coordinated-by-c3d", label: "I need fitment coordinated by C3D Works" }
  ];

  const timelineOptions = [
    { value: "2-4-days", label: "2-4 days" },
    { value: "1-2-weeks", label: "1-2 weeks" },
    { value: "1-month-plus", label: "1 month or more" },
    { value: "other", label: "Other - please specify" }
  ];

  const optionalServicesOptions = [
    { value: "technical-drawings", label: "Technical drawings" },
    { value: "renders", label: "Renders" },
    { value: "production-parts", label: "Production of final parts" }
  ];

  const steps = [
    { title: "Project Details", icon: FileText },
    { title: "Application", icon: Palette },
    { title: "Requirements", icon: Settings },
    { title: "Timeline", icon: Clock },
    { title: "Contact", icon: User }
  ];

  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Content that's always shown (sidebar + form)
  const formContent = (
    <>
      {/* Progress bar for inline mode */}
      {inline && (
        <div className="px-4 md:px-8 pb-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">Step {currentStep + 1} of {totalSteps}</span>
          </div>
          <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    <div className="flex flex-col lg:flex-row">
      {/* Legend Sidebar */}
      <div className={`
        ${legendOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
        lg:w-64 lg:min-h-full overflow-hidden transition-all duration-300 ease-out
        bg-gradient-to-b from-[#0007b5] via-[#0010d9] to-blue-600
      `} data-testid="design-form-legend-sidebar">
        <div className="p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Steps</span>
            <button
              type="button"
              onClick={() => setLegendOpen(false)}
              className="lg:hidden p-1 rounded hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4 text-white/80" />
            </button>
          </div>
          <nav className="space-y-2">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === index;
              const isCompleted = currentStep > index;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => goToStep(index)}
                  data-testid={`design-form-step-${index}`}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                    isActive 
                      ? 'bg-white/20 text-white shadow-lg' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isActive ? 'bg-white text-[#0007b5]' : isCompleted ? 'bg-white/30 text-white' : 'bg-white/10 text-white/60'
                  }`}>
                    {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-white/50">Step {index + 1}</p>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 p-4 md:p-8">
        <form onSubmit={handleSubmit}>
          {/* Fixed File Upload Section */}
          <div className="mb-8 pb-8 border-b border-slate-200">
            <div className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-300 ${
              formData.file ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-300 hover:border-[#0007b5] bg-slate-50'
            }`}>
              {formData.file ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{formData.file.name}</p>
                      <p className="text-xs text-slate-500">
                        {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => document.getElementById('design-service-file').click()}
                    className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => document.getElementById('design-service-file').click()}
                    className="inline-flex items-center justify-center px-6 py-3 bg-[#0007b5] text-white rounded-xl hover:bg-[#0007b5]/90 transition-colors font-semibold shadow-lg shadow-[#0007b5]/20"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Reference (Optional)
                  </button>
                  <p className="text-slate-500 mt-3 text-sm">
                    Images, PDFs, CAD files, sketches
                  </p>
                </div>
              )}
              <Input
                id="design-service-file"
                type="file"
                accept=".pdf,.dwg,.dxf,.stl,.obj,.3mf,.ply,.amf,.step,.stp,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
            <div className="flex items-start gap-2 mt-3 text-xs text-slate-500">
              <Lock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <p>Your files are protected. All uploads are confidential and not shared with any third parties.</p>
            </div>
          </div>

          {/* Animated Step Content */}
          <div className="relative overflow-hidden min-h-[280px]">
            {/* Step 0: Project Description */}
            <div 
              className={`transition-all duration-400 ease-out ${
                currentStep === 0 
                  ? 'opacity-100 translate-x-0' 
                  : direction > 0 
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <div className="space-y-2">
                <Label htmlFor="design-description" className="text-base font-semibold text-slate-900">
                  Project Description
                </Label>
                <p className="text-sm text-slate-500 mb-3">Describe your project in detail</p>
                <Textarea
                  id="design-description"
                  placeholder="Tell us about your idea, what you're trying to achieve, and any specific requirements..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  className="resize-none min-h-[180px]"
                  rows={7}
                />
              </div>
            </div>

            {/* Step 1: Application & Configuration */}
            <div 
              className={`transition-all duration-400 ease-out ${
                currentStep === 1 
                  ? 'opacity-100 translate-x-0' 
                  : currentStep > 1
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <div className="space-y-6">
                <div>
                  <Label className="text-base font-semibold text-slate-900">Application</Label>
                  <p className="text-sm text-slate-500 mb-3">What will this design be used for?</p>
                  <Select value={formData.application} onValueChange={(value) => setFormData({...formData, application: value})}>
                    <SelectTrigger className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl">
                      <SelectValue placeholder="Select application type" />
                    </SelectTrigger>
                    <SelectContent>
                      {applicationOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.application === 'other' && (
                    <input
                      type="text"
                      placeholder="Please specify..."
                      value={formData.applicationOther}
                      onChange={(e) => setFormData({...formData, applicationOther: e.target.value})}
                      className="w-full mt-2 px-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-base font-semibold text-slate-900">Configuration</Label>
                  <p className="text-sm text-slate-500 mb-3">Is this a single piece or multiple parts?</p>
                  <Select value={formData.configuration} onValueChange={(value) => setFormData({...formData, configuration: value})}>
                    <SelectTrigger className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl">
                      <SelectValue placeholder="Select configuration" />
                    </SelectTrigger>
                    <SelectContent>
                      {configurationOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Step 2: Requirements */}
            <div 
              className={`transition-all duration-400 ease-out ${
                currentStep === 2 
                  ? 'opacity-100 translate-x-0' 
                  : currentStep > 2
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <div className="space-y-6">
                <div>
                  <Label className="text-base font-semibold text-slate-900">3D Printing Requirements</Label>
                  <p className="text-sm text-slate-500 mb-3">What's the end goal for this design?</p>
                  <Select value={formData.printingRequirements} onValueChange={(value) => setFormData({...formData, printingRequirements: value})}>
                    <SelectTrigger className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl">
                      <SelectValue placeholder="Select printing requirements" />
                    </SelectTrigger>
                    <SelectContent>
                      {printingRequirementsOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-base font-semibold text-slate-900">Fitment Requirements</Label>
                  <p className="text-sm text-slate-500 mb-3">Does this need to fit with other parts?</p>
                  <Select value={formData.fitmentRequirements} onValueChange={(value) => setFormData({...formData, fitmentRequirements: value})}>
                    <SelectTrigger className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl">
                      <SelectValue placeholder="Select fitment requirements" />
                    </SelectTrigger>
                    <SelectContent>
                      {fitmentOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Step 3: Timeline & Services */}
            <div 
              className={`transition-all duration-400 ease-out ${
                currentStep === 3 
                  ? 'opacity-100 translate-x-0' 
                  : currentStep > 3
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <div className="space-y-6">
                <div>
                  <Label className="text-base font-semibold text-slate-900">How soon do you need a concept?</Label>
                  <p className="text-sm text-slate-500 mb-3">This helps us prioritize your project</p>
                  <Select value={formData.timeline} onValueChange={(value) => setFormData({...formData, timeline: value})}>
                    <SelectTrigger className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent>
                      {timelineOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.timeline === 'other' && (
                    <input
                      type="text"
                      placeholder="Please specify timeline..."
                      value={formData.timelineOther}
                      onChange={(e) => setFormData({...formData, timelineOther: e.target.value})}
                      className="w-full mt-2 px-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-base font-semibold text-slate-900">Optional Services</Label>
                  <p className="text-sm text-slate-500 mb-3">Additional services you may need</p>
                  <div className="space-y-3">
                    {optionalServicesOptions.map(option => (
                      <label key={option.value} className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          formData.optionalServices.includes(option.value)
                            ? 'bg-[#0007b5] border-[#0007b5]'
                            : 'border-slate-300 group-hover:border-[#0007b5]'
                        }`}>
                          {formData.optionalServices.includes(option.value) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.optionalServices.includes(option.value)}
                          onChange={() => handleOptionalServiceChange(option.value)}
                          className="hidden"
                        />
                        <span className="text-slate-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4: Contact Info */}
            <div 
              className={`transition-all duration-400 ease-out ${
                currentStep === 4 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <div className="space-y-6">
                <div>
                  <Label htmlFor="design-name" className="text-base font-semibold text-slate-900">
                    Your Name
                  </Label>
                  <p className="text-sm text-slate-500 mb-3">So we know who to contact</p>
                  <input
                    id="design-name"
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl transition-all duration-300 focus:border-[#0007b5] focus:outline-none focus:ring-0 hover:border-slate-300 placeholder-slate-400"
                  />
                </div>
                <div>
                  <Label htmlFor="design-email" className="text-base font-semibold text-slate-900">
                    Email Address
                  </Label>
                  <p className="text-sm text-slate-500 mb-3">Where we'll send updates</p>
                  <input
                    id="design-email"
                    type="email"
                    placeholder="name@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl transition-all duration-300 focus:border-[#0007b5] focus:outline-none focus:ring-0 hover:border-slate-300 placeholder-slate-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={prevStep}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all duration-300 ${
                currentStep === 0 
                  ? 'opacity-0 pointer-events-none' 
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            {currentStep < totalSteps - 1 ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-3 bg-[#0007b5] text-white rounded-xl font-semibold hover:bg-[#000690] transition-all shadow-lg shadow-blue-500/20"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <Button 
                type="submit" 
                className="px-8 py-3 bg-[#0007b5] hover:bg-[#000690] text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/20"
              >
                Submit Design Request
              </Button>
            )}
          </div>
        </form>
        </div>
      </div>
    </>
  );

  // If inline mode, return just the content without wrapper
  if (inline) {
    return formContent;
  }

  // Otherwise return with full container and header
  return (
    <div className="bg-gradient-to-br from-white via-white to-blue-50/30 rounded-2xl shadow-xl border border-slate-200/60 overflow-hidden">
      {/* Header with Progress */}
      <div className="relative px-4 md:px-8 py-6 border-b border-slate-100">
        <div className="absolute top-0 left-0 w-full h-1 bg-slate-200">
          <div 
            className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setLegendOpen(!legendOpen)}
              className="lg:hidden p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
              data-testid="design-form-legend-toggle"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-[#0007b5] flex items-center justify-center shadow-lg shadow-blue-500/20">
              <DraftingCompass className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold text-slate-900 tracking-tight">Custom Design Service</h2>
              <p className="text-xs md:text-sm text-slate-500 hidden sm:block">We'll create a custom 3D model for you</p>
            </div>
          </div>
          <div className="text-sm text-slate-500 font-medium hidden sm:block">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>
      </div>
      {formContent}
    </div>
  );
};

// Page Components
const PageLayout = ({ title, children, navigateHome }) => (
  <div className="min-h-screen bg-white flex flex-col">
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <Button onClick={navigateHome} variant="ghost" className="flex items-center space-x-2">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Button>
          <button 
            onClick={navigateHome}
            className="absolute left-1/2 transform -translate-x-1/2 flex items-center"
          >
            <img 
              src="/logo-nav.png" 
              alt="Create 3D Works Logo" 
              className="h-10 sm:h-12 w-auto object-contain"
              style={{ minWidth: '120px' }}
            />
          </button>
        </div>
      </div>
    </header>
    <div className="flex-grow">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-800 mb-8">{title}</h1>
        {children}
      </div>
    </div>
    <Footer onNavigate={navigateHome} />
  </div>
);

// About Us Page
const AboutUsPage = ({ navigateHome }) => (
  <PageLayout title={<span>About <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span></span>} navigateHome={navigateHome}>
    <div className="prose prose-lg max-w-none">
      <p className="text-xl text-slate-600 mb-8">
        <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> is a leading provider of professional 3D printing services, serving customers worldwide with precision manufacturing solutions. 
        Founded with a mission to make advanced 3D printing accessible to everyone, we combine cutting-edge technology with expert craftsmanship 
        to deliver exceptional results for every project.
      </p>
      
      <h2 className="text-3xl font-bold text-slate-800 mb-6">Our Mission</h2>
      <p className="text-slate-600 mb-8">
        To democratize access to professional-grade 3D printing technology and empower innovators, engineers, and creators 
        to bring their ideas to life with uncompromising quality and precision.
      </p>
      
      <h2 className="text-3xl font-bold text-slate-800 mb-6">Why Choose Us</h2>
      <ul className="text-slate-600 space-y-4">
        <li>• State-of-the-art 3D printing equipment and technology</li>
        <li>• Expert team with years of experience in additive manufacturing</li>
        <li>• Wide range of materials and finishing options</li>
        <li>• Fast turnaround times without compromising quality</li>
        <li>• Competitive pricing and transparent quoting</li>
        <li>• Dedicated customer support throughout your project</li>
      </ul>
    </div>
  </PageLayout>
);

// FAQ Page
const FAQPage = ({ navigateHome }) => (
  <PageLayout title="Frequently Asked Questions" navigateHome={navigateHome}>
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">What file formats do you accept?</h3>
        <p className="text-slate-600">
          We accept all major 3D file formats including STL, OBJ, 3MF, PLY, AMF, and STEP files. 
          Our system can automatically process and analyze these formats to provide instant quotes.
        </p>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">How accurate are your prints?</h3>
        <p className="text-slate-600">
          Our 3D printers achieve accuracy levels of ±0.1mm for most materials and geometries. 
          We use industrial-grade equipment to ensure consistent, high-quality results.
        </p>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">What materials do you offer?</h3>
        <p className="text-slate-600">
          We offer a wide range of materials including PLA, ABS, PETG, TPU, Wood Fill, Metal Fill, 
          and Carbon Fiber composites. Each material has different properties suitable for various applications.
        </p>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">How long does shipping take?</h3>
        <p className="text-slate-600">
          Standard shipping takes 4-6 weeks from order confirmation. Express options are available 
          for faster delivery. Shipping times may vary based on complexity and current order volume.
        </p>
      </div>
    </div>
  </PageLayout>
);

// Terms Page
const TermsPage = ({ navigateHome }) => (
  <PageLayout title="Terms of Service" navigateHome={navigateHome}>
    <div className="prose prose-lg max-w-none text-slate-600">
      <p className="text-lg mb-8">
        By accessing and using Create 3D Works services, you agree to be bound by these Terms of Service and all applicable laws and regulations.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Service Description</h2>
      <p className="mb-6">
        Create 3D Works provides 3D printing services, design consultation, and related manufacturing services. All services are subject to availability and technical feasibility.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Intellectual Property</h2>
      <p className="mb-6">
        You retain all rights to your designs and files. Create 3D Works will not reproduce, distribute, or use your intellectual property without explicit permission.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Quality and Accuracy</h2>
      <p className="mb-6">
        While we strive for the highest quality, 3D printing involves inherent tolerances and limitations. We guarantee reasonable commercial accuracy within stated tolerances.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Payment and Pricing</h2>
      <p className="mb-6">
        Prices are quoted based on current material costs and complexity analysis. Final pricing may be adjusted for unusual complexity or material requirements.
      </p>
    </div>
  </PageLayout>
);

// File Formats Page
const FileFormatsPage = ({ navigateHome }) => (
  <PageLayout title="Supported File Formats" navigateHome={navigateHome}>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">3D Model Formats</h3>
        <ul className="space-y-4">
          <li className="flex items-center space-x-3">
            <Badge className="bg-green-100 text-green-800">STL</Badge>
            <span className="text-slate-600">Standard Tessellation Language - Most common format</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-blue-100 text-blue-800">OBJ</Badge>
            <span className="text-slate-600">Wavefront OBJ - Supports textures and materials</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-purple-100 text-purple-800">3MF</Badge>
            <span className="text-slate-600">3D Manufacturing Format - Microsoft standard</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-orange-100 text-orange-800">PLY</Badge>
            <span className="text-slate-600">Stanford Triangle Format - Polygon file format</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-red-100 text-red-800">AMF</Badge>
            <span className="text-slate-600">Additive Manufacturing File Format</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-indigo-100 text-indigo-800">STEP</Badge>
            <span className="text-slate-600">Standard for Exchange of Product Data - CAD format</span>
          </li>
        </ul>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">File Requirements</h3>
        <ul className="space-y-3 text-slate-600">
          <li>• Maximum file size: 100MB</li>
          <li>• Minimum feature size: 0.5mm</li>
          <li>• Recommended layer height: 0.1-0.3mm</li>
          <li>• Models should be watertight (closed meshes)</li>
          <li>• Complex geometries may require support structures</li>
          <li>• High-resolution models preferred for detailed parts</li>
        </ul>
      </div>
    </div>
  </PageLayout>
);

// Material Guide Page
const MaterialGuidePage = ({ navigateHome }) => (
  <PageLayout title="Material Selection Guide" navigateHome={navigateHome}>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-green-700">PLA</CardTitle>
          <CardDescription>Biodegradable thermoplastic</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>• Easy to print, low warping</li>
            <li>• Good for prototypes and display models</li>
            <li>• Print temperature: 190-220°C</li>
            <li>• Biodegradable and recyclable</li>
          </ul>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-blue-700">ABS</CardTitle>
          <CardDescription>Durable engineering plastic</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>• Strong and impact resistant</li>
            <li>• Good for functional parts</li>
            <li>• Print temperature: 220-250°C</li>
            <li>• Requires heated bed</li>
          </ul>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-purple-700">PETG</CardTitle>
          <CardDescription>Chemical resistant plastic</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>• Crystal clear transparency</li>
            <li>• Chemical and impact resistant</li>
            <li>• Print temperature: 220-250°C</li>
            <li>• Food-safe applications</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  </PageLayout>
);

// Quality Standards Page
const QualityStandardsPage = ({ navigateHome }) => (
  <PageLayout title="Quality Standards" navigateHome={navigateHome}>
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Precision Standards</h3>
        <p className="text-slate-600 mb-4">
          We maintain strict quality control processes to ensure every print meets our high standards:
        </p>
        <ul className="text-slate-600 space-y-2">
          <li>• Dimensional accuracy: ±0.1mm for most geometries</li>
          <li>• Surface finish: Ra 6.3μm standard, Ra 1.6μm with post-processing</li>
          <li>• Layer adhesion: &gt;90% of material strength</li>
          <li>• Color consistency: Delta E &lt;2 for colored materials</li>
        </ul>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Quality Control Process</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-[#0007b5]">1</span>
            </div>
            <h4 className="font-semibold text-slate-800 mb-2">Pre-Print Inspection</h4>
            <p className="text-slate-600 text-sm">File analysis and optimization review</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-[#0007b5]">2</span>
            </div>
            <h4 className="font-semibold text-slate-800 mb-2">Print Monitoring</h4>
            <p className="text-slate-600 text-sm">Real-time quality monitoring during production</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-[#0007b5]">3</span>
            </div>
            <h4 className="font-semibold text-slate-800 mb-2">Final Inspection</h4>
            <p className="text-slate-600 text-sm">Dimensional verification and finish quality check</p>
          </div>
        </div>
      </div>
    </div>
  </PageLayout>
);

// Shipping Info Page
const ShippingInfoPage = ({ navigateHome }) => (
  <PageLayout title="Shipping & Returns" navigateHome={navigateHome}>
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Delivery Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-700">Standard Shipping</CardTitle>
              <CardDescription>4-6 weeks delivery</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                Economical shipping option for non-urgent projects. Includes tracking and insurance coverage.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-green-700">Express Shipping</CardTitle>
              <CardDescription>2-3 weeks delivery</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                Expedited processing and shipping for time-sensitive projects. Priority handling throughout production.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Packaging</h3>
        <p className="text-slate-600">
          All parts are carefully packaged with protective materials to ensure safe delivery. 
          Fragile or complex geometries receive additional padding and custom packaging as needed.
        </p>
      </div>
    </div>
  </PageLayout>
);

// Marketplace Route Components

// Designer Application Route

// Creator Profile Route
const CreatorProfileRoute = () => {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [creator, setCreator] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  
  // Edit form state
  const [bio, setBio] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [banner, setBanner] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Check if current user is the creator
  const isOwnProfile = user && creator && user.id === creator.user_id;

  useEffect(() => {
    fetchCreatorProfile();
  }, [username]);

  const fetchCreatorProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/creators/${username}`);
      if (response.ok) {
        const data = await response.json();
        setCreator(data.creator);
        setListings(data.listings);
        setBio(data.creator.bio || '');
      } else {
        console.error('Creator not found');
      }
    } catch (error) {
      console.error('Error fetching creator:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Upload profile picture if selected
      if (profilePicture) {
        const formData = new FormData();
        formData.append('file', profilePicture);
        
        await fetch(`${BACKEND_URL}/api/creator/upload-profile-picture`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      }

      // Upload banner if selected
      if (banner) {
        const formData = new FormData();
        formData.append('file', banner);
        
        await fetch(`${BACKEND_URL}/api/creator/upload-banner`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      }

      // Update bio
      const bioFormData = new FormData();
      bioFormData.append('bio', bio);

      const response = await fetch(`${BACKEND_URL}/api/creator/my-profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: bioFormData
      });

      if (response.ok) {
        setIsEditing(false);
        fetchCreatorProfile(); // Refresh profile
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading creator profile...</p>
        </div>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Creator Not Found</h1>
          <p className="text-gray-600 mb-4">This creator profile doesn't exist.</p>
          <Button onClick={() => navigate('/marketplace')}>Back to Marketplace</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner */}
      <div className="relative h-64 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
        {creator.banner_url && (
          <img 
            src={`${BACKEND_URL}${creator.banner_url}`} 
            alt="Banner" 
            className="w-full h-full object-cover"
          />
        )}
        {isEditing && (
          <div className="absolute bottom-4 right-4">
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setBanner(e.target.files[0])}
              className="bg-white"
            />
          </div>
        )}
      </div>

      {/* Profile Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative -mt-20 pb-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Profile Picture */}
              <div className="relative">
                {creator.profile_picture_url ? (
                  <img 
                    src={`${BACKEND_URL}${creator.profile_picture_url}`}
                    alt={creator.display_name}
                    className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">
                      {creator.display_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                {isEditing && (
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setProfilePicture(e.target.files[0])}
                    className="mt-2"
                  />
                )}
              </div>

              {/* Creator Info */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">{creator.display_name}</h1>
                  {isOwnProfile && !isEditing && (
                    <Button onClick={() => setIsEditing(true)} variant="outline">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                  )}
                  {isEditing && (
                    <div className="flex gap-2">
                      <Button onClick={handleSaveProfile} disabled={uploading}>
                        {uploading ? 'Saving...' : 'Save'}
                      </Button>
                      <Button onClick={() => setIsEditing(false)} variant="outline">
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-gray-600 mb-4">@{creator.username}</p>

                {/* Bio */}
                {isEditing ? (
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell us about yourself and your work..."
                    className="w-full p-3 border rounded-lg resize-none"
                    rows={4}
                  />
                ) : (
                  creator.bio && (
                    <p className="text-gray-700 leading-relaxed mb-4">{creator.bio}</p>
                  )
                )}

                {/* Stats */}
                <div className="flex gap-6 mt-4">
                  <div>
                    <span className="block text-2xl font-bold text-gray-900">{listings.length}</span>
                    <span className="text-sm text-gray-600">Designs</span>
                  </div>
                  <div>
                    <span className="block text-2xl font-bold text-gray-900">{creator.total_sales || 0}</span>
                    <span className="text-sm text-gray-600">Sales</span>
                  </div>
                  <div>
                    <span className="block text-2xl font-bold text-gray-900">
                      {new Date(creator.member_since).getFullYear()}
                    </span>
                    <span className="text-sm text-gray-600">Member Since</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Designs Grid */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Designs by {creator.display_name}</h2>
          
          {listings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No designs available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {listings.map((listing) => (
                <Card 
                  key={listing.id} 
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                >
                  <div className="relative aspect-square bg-gradient-to-br from-blue-100 to-purple-100">
                    {listing.preview_images && listing.preview_images.length > 0 ? (
                      <img 
                        src={listing.preview_images[0]} 
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-16 h-16 text-blue-500" />
                      </div>
                    )}
                    {listing.has_3d_preview && (
                      <Badge className="absolute top-2 right-2 bg-[#0007b5] text-white text-xs">3D</Badge>
                    )}
                    {listing.listing_type === 'assembly' && (
                      <Badge className="absolute top-2 left-2 bg-purple-600 text-white text-xs">Assembly</Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{listing.title}</h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Personal</p>
                        <p className="font-bold text-[#0007b5]">${listing.personal_price}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Business</p>
                        <p className="font-bold text-[#0007b5]">${listing.commercial_price}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export { ComingSoonPage, AppContent, CreatorProfileRoute };
